// TrainingSection.jsx
// AI Training Center — two tabs:
//   1. Company Training: proposals, case studies, decks, white papers → Content Library + all AIs
//   2. Voice Training: Gary's writing samples → Cyrano learns Gary's style

import React, { useState, useRef, useCallback } from 'react';
import './TrainingSection.css';
import { apiFetch } from '../lib/api';
import useTenantVocabulary, { vocabList } from '../hooks/useTenantVocabulary';

const COMPANY_CONTENT_TYPES = [
  'Proposal', 'Case Study', 'Presentation Deck', 'Email Template',
  'Competitive Intel', 'Objection Response', 'LinkedIn Post', 'Reference Only',
];

// Service lines for tagging training material. Was a hardcoded list where four of
// nine entries were insurance policy-administration vendors — Guidewire, Duck
// Creek, Majesco, InsuranceNow — offered to every tenant as though they were that
// tenant's own practice areas. Same rule as exavalu_relevance (migration 0030):
// nothing in the base may carry one customer's market.
//
// The tenant's real service lines come from
// tenants.settings.commercial_profile.service_lines via /api/tenant/vocabulary.
// 'General' stays as the fallback because it is a bucket, not a market — a tenant
// that has configured nothing still needs somewhere to put a document.
const FALLBACK_SERVICE_LINES = ['General'];

const ACCEPTED_TYPES = {
  'application/pdf':                                                  'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/msword':                                               'doc',
  'text/plain':                                                       'txt',
  'text/markdown':                                                    'md',
};

function fileTypeLabel(type) {
  return { pdf: 'PDF', docx: 'Word', pptx: 'PowerPoint', doc: 'Word', txt: 'Text', md: 'Markdown' }[type] || type.toUpperCase();
}

function FileIcon({ type }) {
  const icons = { pdf: '📄', docx: '📝', pptx: '📊', doc: '📝', txt: '📃', md: '📃' };
  return <span className="tr-file-icon">{icons[type] || '📁'}</span>;
}

function UploadItem({ item, onRemove }) {
  return (
    <div className={`tr-upload-item tr-upload-item--${item.status}`}>
      <FileIcon type={item.fileType} />
      <div className="tr-upload-item-info">
        <div className="tr-upload-item-name">{item.name}</div>
        <div className="tr-upload-item-meta">
          {fileTypeLabel(item.fileType)} · {(item.size / 1024).toFixed(0)} KB
        </div>
        {item.status === 'processing' && (
          <div className="tr-upload-item-progress">
            <div className="tr-progress-bar"><div className="tr-progress-fill" /></div>
            <span className="tr-upload-item-status-text">Extracting intelligence...</span>
          </div>
        )}
        {item.status === 'done' && item.result && (
          <div className="tr-upload-item-result">
            <span className="tr-result-badge tr-result-badge--success">✓ Saved to {item.result.destination}</span>
            {item.result.title && <span className="tr-result-title">"{item.result.title}"</span>}
            {item.result.summary && <div className="tr-result-summary">{item.result.summary}</div>}
            {item.result.voiceSummary && <div className="tr-result-summary">{item.result.voiceSummary}</div>}
            {item.result.patternsFound > 0 && (
              <span className="tr-result-badge tr-result-badge--info">{item.result.patternsFound} voice patterns learned</span>
            )}
          </div>
        )}
        {item.status === 'error' && (
          <div className="tr-upload-item-error">⚠ {item.error}</div>
        )}
      </div>
      {(item.status === 'queued' || item.status === 'done' || item.status === 'error') && (
        <button className="tr-upload-item-remove" onClick={() => onRemove(item.id)}>✕</button>
      )}
    </div>
  );
}

function DropZone({ onFiles, accept, label, sublabel, icon }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => ACCEPTED_TYPES[f.type]);
    if (files.length) onFiles(files);
  }, [onFiles]);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files).filter(f => ACCEPTED_TYPES[f.type]);
    if (files.length) onFiles(files);
    e.target.value = '';
  };

  return (
    <div
      className={`tr-dropzone ${dragging ? 'tr-dropzone--active' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.pptx,.doc,.txt,.md"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
      <div className="tr-dropzone-icon">{icon}</div>
      <div className="tr-dropzone-label">{label}</div>
      <div className="tr-dropzone-sub">{sublabel}</div>
      <div className="tr-dropzone-types">PDF · Word · PowerPoint · Text · Markdown</div>
    </div>
  );
}

// ── Company Training Tab ──────────────────────────────────────────────────────
function CompanyTrainingTab() {
  // Service lines from this tenant's own profile, not a vendor list. An EMPTY
  // derived list is honoured; only "not answered yet" falls back to General.
  const tenantVocab = useTenantVocabulary();
  const SERVICE_LINE_OPTIONS = vocabList(tenantVocab, 'serviceLines', FALLBACK_SERVICE_LINES);
  const [items, setItems]             = useState([]);
  const [contentType, setContentType] = useState('');
  const [serviceLines, setServiceLines] = useState([]);
  const [notes, setNotes]             = useState('');
  const [processing, setProcessing]   = useState(false);

  const toggleServiceLine = (sl) => {
    setServiceLines(prev => prev.includes(sl) ? prev.filter(s => s !== sl) : [...prev, sl]);
  };

  const handleFiles = (files) => {
    const newItems = files.map(f => ({
      id:       Date.now() + Math.random(),
      name:     f.name,
      size:     f.size,
      fileType: ACCEPTED_TYPES[f.type] || 'txt',
      status:   'queued',
      file:     f,
    }));
    setItems(prev => [...prev, ...newItems]);
  };

  const processAll = async () => {
    const queued = items.filter(i => i.status === 'queued');
    if (!queued.length) return;
    setProcessing(true);

    for (const item of queued) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'processing' } : i));
      try {
        const content = await readFileAsBase64(item.file);
        const res = await apiFetch('/api/training/upload', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            trainingType: 'company',
            fileName:     item.name,
            fileType:     item.fileType,
            content,
            contentType:  contentType || undefined,
            serviceLines: serviceLines.length > 0 ? serviceLines : undefined,
            notes:        notes || undefined,
          }),
        });
        const result = await res.json();
        if (result.success) {
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'done', result } : i));
        } else {
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: result.error } : i));
        }
      } catch (err) {
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: err.message } : i));
      }
    }
    setProcessing(false);
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const queuedCount = items.filter(i => i.status === 'queued').length;

  return (
    <div className="tr-tab-content">
      <div className="tr-tab-intro">
        <div className="tr-tab-intro-icon">🏢</div>
        <div>
          <h3 className="tr-tab-intro-title">Company Training</h3>
          <p className="tr-tab-intro-desc">
            Upload proposals, case studies, capability decks, white papers, and competitive intel.
            Victor, Selma, and Cyrano will all learn from this — deal patterns, capabilities, winning angles, and proof points.
          </p>
        </div>
      </div>

      <div className="tr-options-row">
        <div className="tr-option-group">
          <label className="tr-option-label">Content Type <span className="tr-optional">(optional override — AI will detect automatically)</span></label>
          <select className="tr-select" value={contentType} onChange={e => setContentType(e.target.value)}>
            <option value="">Auto-detect</option>
            {COMPANY_CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="tr-option-group">
          <label className="tr-option-label">Service Lines <span className="tr-optional">(optional)</span></label>
          <div className="tr-tag-picker">
            {SERVICE_LINE_OPTIONS.map(sl => (
              <button
                key={sl}
                className={`tr-tag-btn ${serviceLines.includes(sl) ? 'tr-tag-btn--active' : ''}`}
                onClick={() => toggleServiceLine(sl)}
              >
                {sl}
              </button>
            ))}
          </div>
        </div>
        <div className="tr-option-group">
          <label className="tr-option-label">Context Note <span className="tr-optional">(optional — helps the AI understand the document)</span></label>
          <input
            className="tr-input"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Winning proposal, platform migration, $800K deal"
          />
        </div>
      </div>

      <DropZone
        onFiles={handleFiles}
        icon="📚"
        label="Drop files here or click to browse"
        sublabel="Proposals, case studies, decks, white papers, competitive intel"
      />

      {items.length > 0 && (
        <div className="tr-items-list">
          {items.map(item => (
            <UploadItem key={item.id} item={item} onRemove={removeItem} />
          ))}
        </div>
      )}

      {queuedCount > 0 && (
        <button
          className="tr-process-btn"
          onClick={processAll}
          disabled={processing}
        >
          {processing ? '⏳ Processing...' : `🧠 Train AI Team (${queuedCount} file${queuedCount > 1 ? 's' : ''})`}
        </button>
      )}

      <div className="tr-ai-reach">
        <div className="tr-ai-reach-title">Who learns from Company Training</div>
        <div className="tr-ai-reach-pills">
          <span className="tr-ai-pill tr-ai-pill--victor">⚡ Victor — deal patterns, ideal client fit, win/loss signals</span>
          <span className="tr-ai-pill tr-ai-pill--selma">🔍 Selma — capability evidence, research angles, proof points</span>
          <span className="tr-ai-pill tr-ai-pill--cyrano">✍️ Cyrano — messaging angles, tone, winning language</span>
        </div>
      </div>
    </div>
  );
}

// ── Voice Training Tab ────────────────────────────────────────────────────────
function VoiceTrainingTab() {
  const [items, setItems]   = useState([]);
  const [notes, setNotes]   = useState('');
  const [processing, setProcessing] = useState(false);

  const handleFiles = (files) => {
    const newItems = files.map(f => ({
      id:       Date.now() + Math.random(),
      name:     f.name,
      size:     f.size,
      fileType: ACCEPTED_TYPES[f.type] || 'txt',
      status:   'queued',
      file:     f,
    }));
    setItems(prev => [...prev, ...newItems]);
  };

  const processAll = async () => {
    const queued = items.filter(i => i.status === 'queued');
    if (!queued.length) return;
    setProcessing(true);

    for (const item of queued) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'processing' } : i));
      try {
        const content = await readFileAsBase64(item.file);
        const res = await apiFetch('/api/training/upload', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            trainingType: 'voice',
            fileName:     item.name,
            fileType:     item.fileType,
            content,
            notes:        notes || undefined,
          }),
        });
        const result = await res.json();
        if (result.success) {
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'done', result } : i));
        } else {
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: result.error } : i));
        }
      } catch (err) {
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: err.message } : i));
      }
    }
    setProcessing(false);
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const queuedCount = items.filter(i => i.status === 'queued').length;

  return (
    <div className="tr-tab-content">
      <div className="tr-tab-intro">
        <div className="tr-tab-intro-icon">✍️</div>
        <div>
          <h3 className="tr-tab-intro-title">Voice Training</h3>
          <p className="tr-tab-intro-desc">
            Upload emails you've written, LinkedIn posts, proposals you've personally drafted, or call transcripts.
            Cyrano will learn your authentic patterns — then use them to write the <em>best version</em> of your voice,
            not a perfect copy. Your confidence and directness amplified. Your filler phrases edited out.
          </p>
        </div>
      </div>

      <div className="tr-voice-callout">
        <div className="tr-voice-callout-icon">💡</div>
        <div className="tr-voice-callout-text">
          <strong>What Cyrano does with your samples:</strong> Identifies what makes your voice distinctly yours
          (sentence rhythm, how you open, how you close, your domain vocabulary). Then writes drafts that feel
          like the most polished version of you — not a transcript of your habits.
        </div>
      </div>

      <div className="tr-options-row">
        <div className="tr-option-group">
          <label className="tr-option-label">Context Note <span className="tr-optional">(optional — helps Cyrano understand what type of writing this is)</span></label>
          <input
            className="tr-input"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Outreach emails to insurance CIOs, LinkedIn posts 2024-2025"
          />
        </div>
      </div>

      <DropZone
        onFiles={handleFiles}
        icon="🎙️"
        label="Drop your writing samples here or click to browse"
        sublabel="Emails you've written, LinkedIn posts, proposals, call transcripts"
      />

      {items.length > 0 && (
        <div className="tr-items-list">
          {items.map(item => (
            <UploadItem key={item.id} item={item} onRemove={removeItem} />
          ))}
        </div>
      )}

      {queuedCount > 0 && (
        <button
          className="tr-process-btn tr-process-btn--voice"
          onClick={processAll}
          disabled={processing}
        >
          {processing ? '⏳ Analyzing your voice...' : `🎯 Train Cyrano (${queuedCount} file${queuedCount > 1 ? 's' : ''})`}
        </button>
      )}

      <div className="tr-ai-reach">
        <div className="tr-ai-reach-title">Who learns from Voice Training</div>
        <div className="tr-ai-reach-pills">
          <span className="tr-ai-pill tr-ai-pill--cyrano">✍️ Cyrano — learns your authentic voice and writes the best version of it</span>
        </div>
      </div>
    </div>
  );
}

// ── Helper ────────────────────────────────────────────────────────────────────
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TrainingSection() {
  const [activeTab, setActiveTab] = useState('company');

  return (
    <div className="tr-root">
      <div className="tr-header">
        <h2 className="tr-title">AI Training Center</h2>
        <p className="tr-subtitle">
          Everything you upload here makes your AI team smarter and more specific to DigitalMe.
          Company training feeds Victor, Selma, and Cyrano. Voice training teaches Cyrano your style.
        </p>
      </div>

      <div className="tr-tabs">
        <button
          className={`tr-tab ${activeTab === 'company' ? 'tr-tab--active' : ''}`}
          onClick={() => setActiveTab('company')}
        >
          <span className="tr-tab-icon">🏢</span>
          Company Training
          <span className="tr-tab-badge">Victor · Selma · Cyrano</span>
        </button>
        <button
          className={`tr-tab ${activeTab === 'voice' ? 'tr-tab--active' : ''}`}
          onClick={() => setActiveTab('voice')}
        >
          <span className="tr-tab-icon">✍️</span>
          Voice Training
          <span className="tr-tab-badge">Cyrano only</span>
        </button>
      </div>

      <div className="tr-panel">
        {activeTab === 'company' ? <CompanyTrainingTab /> : <VoiceTrainingTab />}
      </div>
    </div>
  );
}
